// src/components/WeatherWidget.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./WeatherWidget.css";

/* Weather comes from /api/weather (netlify/functions/weather.js). The key used
   to sit in this file and therefore in the shipped bundle, and location came
   from a third-party IP lookup; both now happen server-side. */
const WEATHER_ENDPOINT = "/api/weather";

/* ------------- helper: turn “build / building” into links ------------- */
function linkify(text) {
  return text.split(/(\bbuild(?:ing)?\b)/i).map((part, idx) =>
    /\bbuild(?:ing)?\b/i.test(part) ? (
      <Link key={idx} to="/build" className="wx-link">
        {part}
      </Link>
    ) : (
      part
    )
  );
}

/* ------------- cheeky blurbs by weather group ------------- */
const messageByIcon = {
  Thunderstorm: "Stormy skies – stay safe, stay in, and Get Building with us!",
  Drizzle:      "Not great out – stay in and Get Building with us!",
  Rain:         "Don't get wet – stay cozy and Get Building with us.",
  Snow:         "Snowed in – Get Building with us.",
  Clear:        "Get Building and then go outside.",
  Clouds:       "0% chance of sunburn, Get Building then go outside.",
  Mist:         "Too misty to see? Stay in and Get Building with us.",
  Smoke:        "Too smokey to see? Stay in and Get Building with us.",
  Haze:         "Too hazey to see? Stay in and Get Building with us.",
  Dust:         "Too dusty to see? Stay in and Get Building with us.",
  Fog:          "Too foggy to see? Stay in and Get Building with us.",
  Sand:         "Sandy breeze – stay in and Get Building with us",
  Ash:          "Ash in the air – Get Building from indoors.",
  Squall:       "Blustery squall – anchor down, Get Building.",
  Tornado:      "Tornado watch – spin up ideas, Get Building."
};

export default function WeatherWidget() {
  const [data,   setData]   = useState(null);
  const [status, setStatus] = useState("loading"); // loading • ready • error

  useEffect(() => {
    /* Abort on unmount so a slow response can't resolve into a dead component. */
    const ac = new AbortController();

    fetch(WEATHER_ENDPOINT, { signal: ac.signal })
      .then((r) => {
        /* 503 = key not configured, 204 = edge had no location for this visitor */
        if (!r.ok || r.status === 204) throw new Error(`weather ${r.status}`);
        return r.json();
      })
      .then((w) => {
        if (!w || typeof w.temp !== "number") throw new Error("bad payload");
        setData(w);
        setStatus("ready");
      })
      .catch((err) => {
        if (err.name !== "AbortError") setStatus("error");
      });

    return () => ac.abort();
  }, []);

  /* ---------- UI states ---------- */
  if (status === "loading") {
    return <aside className="wx-card wx-loading">Loading weather…</aside>;
  }

  if (status === "error" || !data) {
    return (
      <aside className="wx-card wx-error">
        Weather unavailable — but it’s always a good day to get building!
      </aside>
    );
  }

  /* ---------- success ---------- */
  const { main, description } = data;
  const temp = Math.round(data.temp);

  const blurb =
    temp < 10
      ? "Cool outside – stay in and Get Building with us."
      : messageByIcon[main] || "Let’s build something today!";

  const locationLabel = data.location ? ` · ${data.location}` : "";

  return (
    <aside className="wx-card" aria-label="local weather">
      <div className="wx-main">
        {temp}°C&nbsp;·&nbsp;{description}
        {locationLabel}
      </div>
      <p className="wx-blurb">{linkify(blurb)}</p>
    </aside>
  );
}
